import { getHapticsEnabled } from "@/lib/haptics-storage";

export type HapticPattern =
	| "selection"
	| "tap"
	| "impact"
	| "success"
	| "warning"
	| "error";

const PATTERNS: Record<HapticPattern, number | number[]> = {
	selection: 5,
	tap: 10,
	impact: 20,
	success: [10, 40, 10],
	warning: [20, 60, 20],
	error: [40, 30, 40, 30, 40],
};

let iosSwitch: HTMLInputElement | null = null;
let iosSupportChecked = false;
let iosSupported = false;

function canVibrate(): boolean {
	return (
		typeof navigator !== "undefined" && typeof navigator.vibrate === "function"
	);
}

function isIOS(): boolean {
	if (typeof navigator === "undefined") return false;
	const ua = navigator.userAgent;
	const platform = navigator.platform ?? "";
	return (
		/iPad|iPhone|iPod/.test(ua) ||
		(platform === "MacIntel" && navigator.maxTouchPoints > 1)
	);
}

function supportsIOSSwitchHaptic(): boolean {
	if (iosSupportChecked) return iosSupported;
	iosSupportChecked = true;
	if (typeof document === "undefined") return false;
	if (!isIOS()) return false;
	try {
		const el = document.createElement("input");
		el.type = "checkbox";
		el.setAttribute("switch", "");
		iosSupported = el.matches("input[switch]");
	} catch {
		iosSupported = false;
	}
	return iosSupported;
}

function getIOSSwitch(): HTMLInputElement | null {
	if (iosSwitch) return iosSwitch;
	if (typeof document === "undefined") return null;
	const el = document.createElement("input");
	el.type = "checkbox";
	el.setAttribute("switch", "");
	el.setAttribute("aria-hidden", "true");
	el.tabIndex = -1;
	el.style.cssText =
		"position:fixed;top:0;left:0;width:0;height:0;opacity:0;pointer-events:none;";
	document.body.appendChild(el);
	iosSwitch = el;
	return el;
}

function prefersReducedMotion(): boolean {
	if (typeof window === "undefined" || !window.matchMedia) return false;
	try {
		return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
	} catch {
		return false;
	}
}

export function isHapticsSupported(): boolean {
	return canVibrate() || supportsIOSSwitchHaptic();
}

export function haptic(pattern: HapticPattern): void {
	if (typeof window === "undefined") return;
	if (!getHapticsEnabled()) return;
	if (prefersReducedMotion()) return;

	if (canVibrate()) {
		try {
			navigator.vibrate(PATTERNS[pattern]);
		} catch {
			/* noop */
		}
		return;
	}

	if (supportsIOSSwitchHaptic()) {
		const sw = getIOSSwitch();
		if (!sw) return;
		try {
			sw.checked = !sw.checked;
		} catch {
			/* noop */
		}
	}
}
